import { Action, Alert, Button, Row, Tile } from '@op/opds.opux';
import { IconDelete } from '@op/opds.opux-icons/action';
import { IconSolution } from '@op/opds.opux-icons/misc';
import { Modal } from '@op/opds.opux-modal';
import React from 'react';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_DELETE_INSURANCE_TARGET } from '../../constants/actionTypes';
import { useInsuranceOffer } from '../../hooks';

const InsuranceQuestionsTile = ({ insurance, name }) => {
  const { state, createOrDeleteInsurance } = useInsuranceOffer();

  const deleteInsuranceTarget = (insuranceId) => {
    createOrDeleteInsurance({
      type: IO_DELETE_INSURANCE_TARGET,
      insuranceId,
    });
  };

  const ibRef = React.createRef();
  const hasVehicle = insurance.type === 'vehicle';
  const hasTravel = insurance.type === 'travel';
  const getMessage = useMessageGetter();

  return (
    <React.Fragment>
      {hasVehicle && (
        <Row around className="RS-insurance-questions-tile">
          <Row around className="vehicle-icon-row">
            <Alert
              inline="large"
              icon={IconSolution}
              className="icon-color-default"
            >
              <Message id="labelText.bannerTextForTotalOffer" /><br/><br/>
              <Action href={getMessage("labelText.aspaLinkUrl")}> <Message id="labelText.aspaLinkText" /></Action>
            </Alert>
          </Row>
        </Row>
      )}
      {hasTravel && (
        <Row around className="RS-insurance-questions-tile">
          <Row around className="vehicle-icon-row">
            <Alert
              inline="large"
              icon={IconSolution}
              className="icon-color-default"
            >
              <Message id="labelText.bannerTextForFixedTermTravelInsurance" /><br/><br/>
              <Action href={getMessage("labelText.fixedTermLinkUrl")}> <Message id="labelText.fixedTemLinkText" /></Action>
            </Alert>
          </Row>
        </Row>
      )}

      <Tile
        dataKey={insurance.id}
        className={`RS-insurance-questions-tile ${
          !hasVehicle && 'vehicle-icon-row'
        }`}
      >
        <Row className="infobox-delete-panel">
          <div className="infobox-insurance-name-text">
            <insurance.icon
              viewBox={'-15 -15 100 100'}
              className={`RS-insurance-questions-icon`}
            />
            <h3
              className={'RS-insurance-questions-header'}
              id={`insurance-h3-${insurance.id}`}
              tabIndex="-1"
            >
              <Message id={`uiText.${insurance.type}`} />
            </h3>
          </div>
          <span className="infobox-delete-button-text">
            <Button
              type="button"
              icon={IconDelete}
              iconLabel="delete"
              iconProps={{ 'aria-hidden': true }}
              minified="true"
              compact
              tabIndex="0"
              className="infobox-delete"
              id={`${name}-infobox-delete`}
              onClick={() => ibRef.current.open()}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  ibRef.current.open();
                }
              }}
            />
            <Modal
              className="info-box-delete-incurance"
              ref={ibRef}
              id="infobox-delete-dialog"
              shouldReturnFocusAfterClose={false}
              title={
                <span className={'info-box-tile-active-text'}>
                  <Message id={`uiText.questionsInfoBox`} />
                </span>
              }
              footer={
                <div className={'info-box-delete-insurance-buttonrow'}>
                  <Button
                    type="button"
                    id="infobox-delete-yes"
                    compact
                    className="info-box-add-insurance-button"
                    onClick={() => {
                      const currentIndex = state.selectedInsurances.findIndex(
                        (item) => item.id === insurance.id
                      );
                      const hasNext =
                        currentIndex > -1 &&
                        currentIndex < state.selectedInsurances.length - 1;
                      const hasPrev = currentIndex > 0;

                      // Proactively blur the trigger to avoid modal restoring focus to it
                      const trigger = document.getElementById(`${name}-infobox-delete`);
                      if (trigger && typeof trigger.blur === 'function') {
                        trigger.blur();
                      }

                      ibRef.current.close();
                      deleteInsuranceTarget(insurance.id);

                      // Outrun modal's potential return-focus by delaying to the next macrotask
                      setTimeout(() => {
                        requestAnimationFrame(() => {
                          const headers = document.querySelectorAll(
                            '.RS-insurance-questions-header'
                          );

                          let targetIndex = null;
                          if (hasNext) {
                            targetIndex = currentIndex; // next item shifts into current index
                          } else if (hasPrev) {
                            targetIndex = currentIndex - 1; // focus previous item
                          }

                          if (targetIndex !== null) {
                            const target = headers[targetIndex];
                            if (target && typeof target.focus === 'function') {
                              target.focus();
                              return;
                            }
                          }

                          // Fallback: focus InsuranceSelectorRow when no headers available
                          const fallback = document.getElementById('InsuranceSelectorRow');
                          if (fallback) {
                            if (!fallback.hasAttribute('tabindex')) {
                              fallback.setAttribute('tabindex', '-1');
                            }
                            fallback.focus();
                          } 
                        });
                      }, 0);
                    }}
                  >
                    <Message id={`buttonText.yes`} />
                  </Button>
                  <Button
                    type="button"
                    id="infobox-delete-no"
                    compact
                    className="info-box-add-insurance-button"
                    onClick={() => {
                      ibRef.current.close();
                      // After cancel, restore focus to the delete trigger
                      setTimeout(() => {
                        const trigger = document.getElementById(
                          `${name}-infobox-delete`
                        );
                        if (trigger && typeof trigger.focus === 'function') {
                          trigger.focus();
                        }
                      }, 0);
                    }}
                  >
                    <Message id={`buttonText.no`} />
                  </Button>
                </div>
              }
            />
            <label
              htmlFor={`${name}-infobox-delete`}
              id={`insurance-name-${name}`}
            >
              <Message id="uiText.deleteInsuranceQuestions" />
            </label>
          </span>
        </Row>
        <insurance.component {...insurance} />
      </Tile>
    </React.Fragment>
  );
};

export default InsuranceQuestionsTile;
